/**
 * Settings API client — typed helpers for the public storefront-facing
 * `GET /api/v1/settings/*` endpoints.
 *
 * Currently exposes a single helper, {@link fetchShippingSettings}, used by
 * SF-SHIP-002's free-shipping indicator. Future settings endpoints (e.g.
 * `/settings/checkout`, `/settings/branding`) hang off the same module so
 * containers have one import target.
 *
 * Patterns mirror `@dfq/shared/api/shipping`:
 *  - Calls the configured shared axios instance — no per-call auth headers
 *    or baseURL overrides.
 *  - Unwraps the success envelope into a typed domain value via the shared
 *    `unwrap` helper.
 *  - Translates non-2xx responses into `ApiError` via `throwAsApiError` so
 *    callers (slices, container effects) can `catch (e instanceof ApiError)`
 *    uniformly.
 *
 * The free-shipping indicator MUST degrade silently when this endpoint
 * fails — callers therefore wrap the call in `try/catch` and treat any
 * error as "no threshold configured." That fallback policy lives in the
 * caller, not here.
 *
 * SF-SHIP-002.
 */
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  ArtworkSettings,
  ArtworkSettingsWire,
  HomepageSettings,
  HomepageSettingsWire,
  NavigationSettings,
  NavigationSettingsWire,
  QuotesSettings,
  QuotesSettingsWire,
  ShippingSettings,
  ShippingSettingsWire,
} from '@dfq/shared/types/settings';

import { apiClient } from './client';
import { throwAsApiError, unwrap } from './_envelope';

// ----------------------------------------------------------------------------
// Fallback defaults
// ----------------------------------------------------------------------------

/**
 * Fallback values used by containers when `GET /api/v1/settings/shipping`
 * is unavailable (404, network error, endpoint not yet deployed). Keeps cart
 * and checkout surfaces functional with sensible UI copy while the backend
 * endpoint is absent.
 *
 * These are UI-only defaults — they do NOT affect server-side pricing or
 * shipping qualification. Override by returning a real response from the
 * platform endpoint.
 */
export const SHIPPING_SETTINGS_FALLBACK: ShippingSettings = {
  chargeShipping: false,
  freeShippingThreshold: 150,
  rushShippingSurcharge: null,
  carriersEnabled: { ups: true, fedex: false, usps: false },
  defaultLeadTimeDays: null,
  rushLeadTimeDays: null,
};

// ----------------------------------------------------------------------------
// Path builders
// ----------------------------------------------------------------------------

function shippingSettingsPath(): string {
  return '/settings/shipping';
}

function homepageSettingsPath(): string {
  return '/settings/homepage';
}

function navigationSettingsPath(): string {
  return '/settings/navigation';
}

function quotesSettingsPath(): string {
  return '/settings/quotes';
}

function artworkSettingsPath(): string {
  return '/settings/artwork';
}

// ----------------------------------------------------------------------------
// Wire → domain transforms
// ----------------------------------------------------------------------------

/**
 * Map the platform's snake_case wire shape onto the slice/container-facing
 * camelCase shape. Treats missing carrier flags as `false` so consumers can
 * branch without checking for `undefined`.
 */
function fromShippingSettingsWire(wire: ShippingSettingsWire): ShippingSettings {
  return {
    // Absent means not charged: the safe default is the one that does not
    // invent a charge the customer was never quoted.
    chargeShipping: wire.chargeShipping ?? false,
    // The platform emits `freeShippingThresholdCents`; this module's contract
    // (and the FreeShippingIndicator capsule) is decimal dollars.
    freeShippingThreshold:
      typeof wire.freeShippingThresholdCents === 'number'
        ? wire.freeShippingThresholdCents / 100
        : typeof wire.free_shipping_threshold === 'number'
          ? wire.free_shipping_threshold
          : null,
    rushShippingSurcharge:
      typeof wire.rush_shipping_surcharge === 'number'
        ? wire.rush_shipping_surcharge
        : wire.rush_shipping_surcharge ?? null,
    carriersEnabled: {
      ups: wire.carriers_enabled?.ups ?? false,
      fedex: wire.carriers_enabled?.fedex ?? false,
      usps: wire.carriers_enabled?.usps ?? false,
    },
    defaultLeadTimeDays:
      typeof wire.defaultLeadTimeDays === 'number' ? wire.defaultLeadTimeDays : null,
    rushLeadTimeDays:
      typeof wire.rushLeadTimeDays === 'number' ? wire.rushLeadTimeDays : null,
  };
}

/**
 * Fallback used when `GET /api/v1/settings/homepage` is unavailable (404,
 * network error, endpoint not yet deployed). Every field is `null`/empty so
 * the chooser container omits copy it doesn't have rather than inventing
 * placeholder text — see SF-HOME-102's "nothing hardcoded" rule.
 */
export const HOMEPAGE_SETTINGS_FALLBACK: HomepageSettings = {
  heroOverline: null,
  heroHeadline: null,
  heroSubhead: null,
  heroImageUrl: null,
  chooserCardCta: null,
  chooserLinks: [],
  valueProps: [],
  featuredOverline: null,
  featuredHeading: null,
  featuredActionLabel: null,
  instantPriceOverline: null,
  instantPriceNote: null,
  stepsOverline: null,
  stepsHeading: null,
  testimonialsHeading: null,
  steps: [],
  seoDescription: null,
};

/**
 * Normalizes the homepage settings wire payload — guards against a missing
 * `valueProps`/`steps`/`chooserLinks` array (treated as empty) so
 * containers never have to null-check a list field.
 */
function fromHomepageSettingsWire(wire: HomepageSettingsWire): HomepageSettings {
  return {
    heroOverline: wire.heroOverline ?? null,
    heroHeadline: wire.heroHeadline ?? null,
    heroSubhead: wire.heroSubhead ?? null,
    heroImageUrl: wire.heroImageUrl ?? null,
    chooserCardCta: wire.chooserCardCta ?? null,
    chooserLinks: wire.chooserLinks ?? [],
    valueProps: wire.valueProps ?? [],
    featuredOverline: wire.featuredOverline ?? null,
    featuredHeading: wire.featuredHeading ?? null,
    featuredActionLabel: wire.featuredActionLabel ?? null,
    instantPriceOverline: wire.instantPriceOverline ?? null,
    instantPriceNote: wire.instantPriceNote ?? null,
    stepsOverline: wire.stepsOverline ?? null,
    stepsHeading: wire.stepsHeading ?? null,
    testimonialsHeading: wire.testimonialsHeading ?? null,
    steps: wire.steps ?? [],
    seoDescription: wire.seoDescription ?? null,
  };
}

// ----------------------------------------------------------------------------
// Public helpers
// ----------------------------------------------------------------------------

/**
 * `GET /api/v1/settings/shipping`
 *
 * Returns the admin-configured shipping settings (free shipping threshold,
 * rush surcharge, carrier toggles) used by storefront UI surfaces. Public
 * endpoint — no user context required, but client-credential auth (the
 * Storefront OAuth client) is enforced by the platform.
 *
 * `freeShippingThreshold` is `null` when the admin has not configured a
 * threshold; the FE indicator treats `null`, `0`, or any failure as "no
 * promotion active" and renders nothing.
 */
export async function fetchShippingSettings(): Promise<ShippingSettings> {
  try {
    const response = await apiClient.get<ApiEnvelope<{ settings: ShippingSettingsWire }>>(
      shippingSettingsPath(),
    );
    // The envelope nests the payload under `data.settings`.
    const data = unwrap(response.data, response.status);
    return fromShippingSettingsWire(data.settings ?? ({} as ShippingSettingsWire));
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `GET /api/v1/settings/homepage` — SF-HOME-102.
 *
 * Admin-configured copy for the "Start your project" chooser (hero, value
 * strip, popular products / instant price / how-it-works headings, and the
 * numbered steps). Public endpoint, no user context required. Every field
 * may be `null`/empty — the chooser container renders around gaps rather
 * than inventing placeholder copy.
 */
export async function fetchHomepageSettings(): Promise<HomepageSettings> {
  try {
    const response = await apiClient.get<ApiEnvelope<{ homepage: HomepageSettingsWire }>>(
      homepageSettingsPath(),
    );
    const data = unwrap(response.data, response.status);
    return fromHomepageSettingsWire(data.homepage ?? ({} as HomepageSettingsWire));
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * Fallback used when `GET /api/v1/settings/navigation` is unavailable (404,
 * network error, endpoint not yet deployed). Every field is `null`/empty so
 * `PublicLayout`/`CustomerLayout` render only the utility cluster (search,
 * account, cart) rather than inventing nav/footer copy.
 */
export const NAVIGATION_SETTINGS_FALLBACK: NavigationSettings = {
  headerLinks: [],
  footerColumns: [],
  headerLogoUrl: null,
  footerWordmark: null,
  footerTagline: null,
  footerLocation: null,
  footerContact: null,
  footerCopyright: null,
};

/**
 * Normalizes the navigation settings wire payload. The platform degrades
 * missing keys to `[]` for lists and `""` for footer strings (never `null`);
 * this maps `""` to `null` so callers have one representation of "unset".
 */
function fromNavigationSettingsWire(wire: NavigationSettingsWire): NavigationSettings {
  const orNull = (value: string | undefined): string | null => (value ? value : null);
  return {
    headerLinks: wire.headerLinks ?? [],
    footerColumns: wire.footerColumns ?? [],
    headerLogoUrl: orNull(wire.headerLogoUrl),
    footerWordmark: orNull(wire.footerWordmark),
    footerTagline: orNull(wire.footerTagline),
    footerLocation: orNull(wire.footerLocation),
    footerContact: orNull(wire.footerContact),
    footerCopyright: orNull(wire.footerCopyright),
  };
}

/**
 * `GET /api/v1/settings/navigation` — SF-NAV-101.
 *
 * Admin-configured storefront header links + footer columns/copy. Public
 * endpoint, no user context required. `headerLinks`/`footerColumns` entries
 * may carry a `requiresCategory` slug — callers must drop any link whose
 * slug isn't present in the live (visible) category tree before rendering.
 */
export async function fetchNavigationSettings(): Promise<NavigationSettings> {
  try {
    const response = await apiClient.get<ApiEnvelope<{ navigation: NavigationSettingsWire }>>(
      navigationSettingsPath(),
    );
    const data = unwrap(response.data, response.status);
    return fromNavigationSettingsWire(data.navigation ?? ({} as NavigationSettingsWire));
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * Fallback used when `GET /api/v1/settings/quotes` is unavailable (404,
 * network error, endpoint not yet deployed). Every field is `null` so the
 * quote-request page falls back to hardcoded default copy rather than
 * rendering blank headings.
 */
export const QUOTES_SETTINGS_FALLBACK: QuotesSettings = {
  publicPageHeading: null,
  publicPageIntro: null,
  confirmationHeading: null,
  confirmationMessage: null,
  acceptAccountMessage: null,
};

/** Normalizes `""` wire fields (the settings-row convention for "unset") to `null`. */
function fromQuotesSettingsWire(wire: QuotesSettingsWire): QuotesSettings {
  const orNull = (value: string | undefined): string | null => (value ? value : null);
  return {
    publicPageHeading: orNull(wire.publicPageHeading),
    publicPageIntro: orNull(wire.publicPageIntro),
    confirmationHeading: orNull(wire.confirmationHeading),
    confirmationMessage: orNull(wire.confirmationMessage),
    acceptAccountMessage: orNull(wire.acceptAccountMessage),
  };
}

/**
 * `GET /api/v1/settings/quotes` — SF-QUOTE-103.
 *
 * Admin-editable copy for the public quote-request page, its confirmation
 * state, and the sign-in gate shown when a signed-out visitor tries to
 * accept a sent quote. Public endpoint, no user context required.
 */
export async function fetchQuotesSettings(): Promise<QuotesSettings> {
  try {
    const response = await apiClient.get<ApiEnvelope<{ quotes: QuotesSettingsWire }>>(
      quotesSettingsPath(),
    );
    const data = unwrap(response.data, response.status);
    return fromQuotesSettingsWire(data.quotes ?? ({} as QuotesSettingsWire));
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * Fallback used when `GET /api/v1/settings/artwork` is unavailable (404,
 * network error, endpoint not yet deployed). Mirrors the platform DTO's own
 * code-level defaults (`ArtworkSettingsDTO`) so the Custom Shape Editor
 * degrades to the same values the server would have returned, rather than a
 * second, potentially-drifting set of literals.
 */
export const ARTWORK_SETTINGS_FALLBACK: ArtworkSettings = {
  maxUploadSizeBytes: null,
  shapeEditorDefaultCornerRadiusIn: 0.25,
  shapeEditorCanvasMarginMinIn: 2,
  shapeEditorCanvasMarginPercent: 0.25,
  shapeEditorDefaultMaxPrintSizeIn: 24,
  contourCornerStyleDefault: 'as_is',
  contourCornerSmoothingDefault: 0.5,
  contourPreviewCornerStyleDefault: 'as_is',
  contourMiterLimit: 4.0,
  contourMaxRoundingRadiusInches: 0.375,
  browserDecodeMaxSizeBytes: 26_214_400,
};

/**
 * Maps the wire payload to the domain shape. `maxUploadSizeBytes` degrades
 * to `null` ("no client-side cap known") only if the platform response is
 * malformed — a real 200 always carries a number. There is deliberately NO
 * hardcoded byte fallback here: PL-RULES-105 made this an admin setting,
 * and a stale build-time guess would drift from it. The server always
 * re-validates the actual upload regardless of what the client advertises.
 *
 * The shape-editor fields (SH-SHAPE-101/102, SF-SHAPE-103) degrade to
 * `ARTWORK_SETTINGS_FALLBACK`'s values when the wire payload omits them.
 */
function fromArtworkSettingsWire(wire: ArtworkSettingsWire): ArtworkSettings {
  return {
    maxUploadSizeBytes: typeof wire.maxUploadSizeBytes === 'number' ? wire.maxUploadSizeBytes : null,
    shapeEditorDefaultCornerRadiusIn:
      typeof wire.shapeEditorDefaultCornerRadiusIn === 'number'
        ? wire.shapeEditorDefaultCornerRadiusIn
        : ARTWORK_SETTINGS_FALLBACK.shapeEditorDefaultCornerRadiusIn,
    shapeEditorCanvasMarginMinIn:
      typeof wire.shapeEditorCanvasMarginMinIn === 'number'
        ? wire.shapeEditorCanvasMarginMinIn
        : ARTWORK_SETTINGS_FALLBACK.shapeEditorCanvasMarginMinIn,
    shapeEditorCanvasMarginPercent:
      typeof wire.shapeEditorCanvasMarginPercent === 'number'
        ? wire.shapeEditorCanvasMarginPercent
        : ARTWORK_SETTINGS_FALLBACK.shapeEditorCanvasMarginPercent,
    shapeEditorDefaultMaxPrintSizeIn:
      typeof wire.shapeEditorDefaultMaxPrintSizeIn === 'number'
        ? wire.shapeEditorDefaultMaxPrintSizeIn
        : ARTWORK_SETTINGS_FALLBACK.shapeEditorDefaultMaxPrintSizeIn,
    contourCornerStyleDefault: wire.contourCornerStyleDefault ?? ARTWORK_SETTINGS_FALLBACK.contourCornerStyleDefault,
    contourCornerSmoothingDefault:
      typeof wire.contourCornerSmoothingDefault === 'number'
        ? wire.contourCornerSmoothingDefault
        : ARTWORK_SETTINGS_FALLBACK.contourCornerSmoothingDefault,
    contourPreviewCornerStyleDefault:
      wire.contourPreviewCornerStyleDefault ?? ARTWORK_SETTINGS_FALLBACK.contourPreviewCornerStyleDefault,
    contourMiterLimit:
      typeof wire.contourMiterLimit === 'number'
        ? wire.contourMiterLimit
        : ARTWORK_SETTINGS_FALLBACK.contourMiterLimit,
    contourMaxRoundingRadiusInches:
      typeof wire.contourMaxRoundingRadiusInches === 'number'
        ? wire.contourMaxRoundingRadiusInches
        : ARTWORK_SETTINGS_FALLBACK.contourMaxRoundingRadiusInches,
    browserDecodeMaxSizeBytes:
      typeof wire.browserDecodeMaxSizeBytes === 'number'
        ? wire.browserDecodeMaxSizeBytes
        : ARTWORK_SETTINGS_FALLBACK.browserDecodeMaxSizeBytes,
  };
}

/**
 * `GET /api/v1/settings/artwork` — PL-RULES-105.
 *
 * Admin-configured global artwork upload size limit. Public endpoint, no
 * user context required — the storefront configurator and account artwork
 * library read this before/without a signed-in session. A per-product-type
 * cap may still apply on top of this when it is lower. Callers should treat
 * a rejected promise (network error, endpoint unavailable) as "no
 * client-side cap known" — see `useArtworkUploadLimit`.
 */
export async function fetchArtworkSettings(): Promise<ArtworkSettings> {
  try {
    const response = await apiClient.get<ApiEnvelope<{ artwork: ArtworkSettingsWire }>>(
      artworkSettingsPath(),
    );
    const data = unwrap(response.data, response.status);
    return fromArtworkSettingsWire(data.artwork ?? ({} as ArtworkSettingsWire));
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * Convenience namespace export for callers that prefer
 * `import { settingsApi } from '@dfq/shared/api/settings'`.
 */
export const settingsApi = {
  fetchShippingSettings,
  fetchHomepageSettings,
  fetchNavigationSettings,
  fetchQuotesSettings,
  fetchArtworkSettings,
};
